import { CELL, GROUND } from '../core/config';
import { furnish } from './furniture';
import { prepareRoomLayouts } from './room-layouts';
import {
  key,
  MATERIALS,
  type Building,
  type Cell,
  type City,
  type Interior,
  type Place,
  type PlaceKind,
} from './city';

/** Occupied subset is explicit in the manifest, never inferred from windows. */
export function prepareLivingDistrict(city: City): City {
  if (city.interiors) return city;
  city.interiors = [];
  city.places = [];
  const near = (kind: string) =>
    city.buildings
      .filter(
        (b) =>
          b.kind === kind &&
          !city.buildings.some(
            (other) =>
              other !== b &&
              other.x < b.x + 3.2 &&
              other.x + other.w * CELL > b.x + 0.8 &&
              other.z < b.z &&
              other.z + other.d * CELL > b.z - 2,
          ),
      )
      .sort((a, b) => Math.hypot(a.x + 55, a.z + 40) - Math.hypot(b.x + 55, b.z + 40))[0];
  const selected: [Building | undefined, Interior['use'], number[]][] = [
    [near('glass-office'), 'office', [0, 1]],
    [near('modern-apartment'), 'apartment', [0, 1]],
    [near('corner-shop'), 'shop', [0]],
    [near('warehouse'), 'workshop', [0]],
  ];
  if (city.regional) {
    let additionalCentral = 0;
    for (const b of city.buildings) {
      if (
        selected.some(([old]) => old === b) ||
        ['grain-silo', 'hangar', 'round-civic', 'chapel', 'barn', 'u-school'].includes(b.kind)
      )
        continue;
      if (Math.abs(b.x) < 215 && Math.abs(b.z) < 215) {
        if (
          additionalCentral >= 24 ||
          city.buildings.some(
            (other) =>
              other !== b &&
              other.x < b.x + 3.2 &&
              other.x + other.w * CELL > b.x + 0.8 &&
              other.z < b.z &&
              other.z + other.d * CELL > b.z - 2,
          )
        )
          continue;
        additionalCentral++;
      }
      const use: Interior['use'] = [
        'pitched-house',
        'semi-detached',
        'rowhouse',
        'slab-apartment',
        'stepped-apartment',
        'modern-apartment',
      ].includes(b.kind)
        ? 'apartment'
        : ['warehouse', 'sawtooth-factory'].includes(b.kind)
          ? 'workshop'
          : ['corner-shop', 'market-hall', 'mall'].includes(b.kind)
            ? 'shop'
            : 'office';
      selected.push([b, use, (b.roofLevel ?? 3) >= 6 ? [0, 1] : [0]]);
    }
  }
  for (const b of city.buildings)
    if (b.landmark) {
      const prior = selected.find((entry) => entry[0] === b);
      if (prior) prior[2] = b.landmark.occupiedFloors;
      else selected.push([b, 'office', b.landmark.occupiedFloors]);
    }
  let id =
    Math.max(
      ...[
        ...city.buildings,
        ...(city.props ?? []),
        ...(city.bridges ?? []),
        ...(city.infrastructure ?? []),
      ].map((b) => b.id),
    ) + 1;
  city.props ??= [];
  for (const [b, use, floors] of selected) {
    if (!b) continue;
    const floorHeight = ['warehouse', 'civic', 'hangar', 'market-hall', 'sawtooth-factory'].includes(b.kind)
      ? 4
      : 3;
    if (!b.landmark && (!city.regional || (Math.abs(b.x) < 215 && Math.abs(b.z) < 215)))
      b.name =
        use === 'office'
          ? 'Juniper House · offices'
          : use === 'apartment'
            ? 'Linden Court · apartments'
            : use === 'shop'
              ? 'Corner Market'
              : 'Canal Works';
    b.cells = b.cells.filter((c) => !(c.z === 0 && [1, 2].includes(c.x) && c.y > 0 && c.y < floorHeight));
    for (const x of [1, 2])
      if (!b.cells.some((c) => c.x === x && c.y === 0 && c.z === 0))
        b.cells.push({
          id: b.id * 1000000 + key(x, 0, 0),
          x,
          y: 0,
          z: 0,
          hp: 2.6,
          material: 'concrete',
          role: 'slab',
          color: 0xcdd0c3,
        });
    for (const c of b.cells) {
      if (c.role === 'beam' && c.y % floorHeight === floorHeight - 1)
        c.bounds = { x: 0, y: 0.68, z: 0, w: 1, h: 0.32, d: 1 };
      if (c.role === 'column' && c.y % floorHeight !== 0)
        c.bounds = { x: 0.2, y: 0, z: 0.2, w: 0.6, h: 1, d: 0.6 };
    }
    // Occupied facades need actual panes, not dark rectangles painted on solid
    // brick. Keep the structural piers/slabs and each existing facade ID.
    for (const c of b.cells) {
      if (!floors.includes(Math.floor(c.y / floorHeight)) || c.y % floorHeight === 0) continue;
      if (c.role === 'facade' && c.material === 'brick') {
        c.windowTrim = c.color;
        c.material = 'glass';
        c.role = 'glass';
        c.hp = MATERIALS.glass.strength;
        c.color = 0x719798;
      }
      if (c.material !== 'glass') continue;
      if (c.z === 0 || c.z === b.d - 1)
        c.bounds = { x: 0, y: 0, z: c.z === 0 ? 0 : 0.93, w: 1, h: 1, d: 0.07 };
      else if (c.x === 0 || c.x === b.w - 1)
        c.bounds = { x: c.x === 0 ? 0 : 0.93, y: 0, z: 0, w: 0.07, h: 1, d: 1 };
    }
    if (b.kind === 'glass-office')
      for (let x = 3; x < b.w - 1; x++)
        for (let y = 1; y < floorHeight; y++)
          if (!b.cells.some((c) => c.x === x && c.y === y && c.z === 0))
            b.cells.push({
              id: b.id * 1e6 + key(x, y, 0),
              x,
              y,
              z: 0,
              hp: MATERIALS.glass.strength,
              material: 'glass',
              role: 'glass',
              color: 0x719798,
              bounds: { x: 0, y: 0, z: 0, w: 1, h: 1, d: 0.07 },
            });
    const entrance = { x: b.x + 1.5 * CELL, y: GROUND, z: b.z - 1.6 };
    city.interiors.push({
      buildingId: b.id,
      use,
      floors,
      floorHeight,
      doorX: 1,
      entrance,
      liftX: b.w - 2,
      liftZ: b.d - 2,
    });
    const stairs: Building = {
      id: id++,
      name: `${b.name} entrance steps`,
      kind: 'road',
      category: 'prop',
      x: entrance.x - 0.625,
      z: b.z - 1.5,
      baseY: GROUND,
      w: 2,
      d: 2,
      h: 1,
      color: 0xbec2b2,
      accent: 0xbec2b2,
      cells: [],
      chunk: b.chunk,
    };
    for (let s = 0; s < 5; s++)
      for (let x = 0; x < 2; x++)
        stairs.cells.push({
          id: stairs.id * 1000000 + key(x, 0, s),
          x,
          y: 0,
          z: s,
          hp: 2.6,
          material: 'concrete',
          role: 'slab',
          root: true,
          color: 0xc3c5b8,
          bounds: { x: 0, y: 0, z: s * (0.3 / CELL - 1), w: 1, h: ((s + 1) * 0.25) / CELL, d: 0.3 / CELL },
        });
    city.props.push(stairs);
    for (const floor of floors) {
      const floorY = floor * floorHeight,
        foot = (b.baseY ?? GROUND) + (floorY + 1) * CELL;
      const choices: { x: number; z: number }[] = [];
      for (let row = 2; row < b.d - 2; row += 3)
        for (let x = 2; x < b.w - 2; x += 3) {
          // Leave the entrance/exit corridor usable while the first workstation
          // is occupied. Moving the front row preserves its item count.
          const z = floor === 0 && row === 2 ? 3 : row;
          if (b.id === city.airport?.terminal && x >= 16) continue;
          // Keep the cross-corridor ahead of a T-plan core clear of furniture.
          if (b.kind === 't-office' && z === 5) continue;
          const occupied = (xx: number, zz: number) =>
            b.cells.some((c) => {
              if (c.x !== xx || c.z !== zz || c.y <= floorY) return false;
              const shape = c.bounds ?? { x: 0, y: 0, z: 0, w: 1, h: 1, d: 1 };
              return (
                c.y + shape.y < floorY + 1 + 1.85 / CELL &&
                shape.x < 0.7 &&
                shape.x + shape.w > 0.3 &&
                shape.z < 0.7 &&
                shape.z + shape.d > 0.3
              );
            });
          if (
            !occupied(x, z) &&
            !occupied(x, z - 1) &&
            b.cells.some((c) => c.x === x && c.z === z && c.y === floorY) &&
            b.cells.some((c) => c.x === x && c.z === z - 1 && c.y === floorY) &&
            b.cells.some((c) => c.x === x && c.z === z && c.y === floorY + floorHeight)
          )
            choices.push({ x, z });
        }
      const count = Math.min(
          choices.length,
          city.regional
            ? Math.max(8, Math.min(24, Math.floor(((b.w - 2) * (b.d - 2)) / 14)))
            : use === 'apartment'
              ? 4
              : 8,
        ),
        arranged = Array.from({ length: count }, (_, i) => choices[Math.floor((i * choices.length) / count)]),
        tenants = new Set<string>();
      for (const [index, p] of arranged.entries()) {
        const tenant =
          b.kind === 'mall'
            ? `tenant:${b.id}:${floor}:${p.x < b.w / 2 ? 'west' : 'east'}:${p.z < b.d / 2 ? 'north' : 'south'}`
            : `tenant:${b.id}:${floor}`;
        const kind: PlaceKind =
          use === 'office'
            ? index % 4 === 3
              ? 'seat'
              : 'desk'
            : use === 'apartment'
              ? 'home'
              : use === 'shop'
                ? !tenants.has(tenant)
                  ? 'counter'
                  : 'display'
                : index % 2
                  ? 'workbench'
                  : 'materials';
        let furniture: Building['furniture'] =
          kind === 'desk'
            ? 'desk'
            : kind === 'home'
              ? index % 2
                ? 'sofa'
                : 'bed'
              : kind === 'display'
                ? 'shelf'
                : kind === 'counter'
                  ? 'counter'
                  : kind === 'materials'
                    ? 'crate'
                    : kind === 'workbench'
                      ? 'workbench'
                      : 'chair';
        tenants.add(tenant);
        let bedAlongRoom = false;
        if (furniture === 'bed') {
          const free = (x: number, z: number) =>
            b.cells.some((c) => c.x === x && c.z === z && c.y === floorY) &&
            !b.cells.some(
              (c) =>
                c.x === x &&
                c.z === z &&
                c.y > floorY &&
                c.y < floorY + floorHeight &&
                (c.bounds?.y ?? 0) * CELL + (c.y - floorY - 1) * CELL < 1.75,
            );
          // A bed spans two grid slots. A free anchor slot alone does not prove
          // that the mattress or its original occupant clears an adjacent core.
          let fitted = false;
          for (const along of [false, true]) {
            const row = [p.z, p.z - 1, p.z + 1].find((z) => {
              if (z < 2 || z >= b.d - 2 || !free(p.x, z) || !free(p.x, z - 1)) return false;
              if (along ? !free(p.x, z + 1) : !free(p.x + 1, z) || !free(p.x + 1, z - 1)) return false;
              const maxX = p.x + (along ? 1.15 : 2.02) / CELL,
                maxZ = z + (along ? 2.02 : 1.15) / CELL,
                margin = 0.24 / CELL;
              return !choices.some(
                (other) =>
                  other !== p &&
                  other.x + 0.5 > p.x + 0.1 / CELL - margin &&
                  other.x + 0.5 < maxX + margin &&
                  other.z - 0.5 > z + 0.1 / CELL - margin &&
                  other.z - 0.5 < maxZ + margin,
              );
            });
            if (row !== undefined) {
              p.z = row;
              bedAlongRoom = along;
              fitted = true;
              break;
            }
          }
          // A narrow sitting room receives a usable sofa; never author an
          // impossible bed through a column and leave its resident stuck there.
          if (!fitted) furniture = 'sofa';
        }
        const floorCell = b.cells.find((c) => c.x === p.x && c.z === p.z && c.y === floorY)!;
        const itemId = id++,
          height =
            furniture === 'shelf' ? 1.5 : furniture === 'bed' ? 0.55 : furniture === 'crate' ? 0.7 : 0.78;
        const itemCells: Cell[] = [
          {
            id: itemId * 1000000,
            x: 0,
            y: 0,
            z: 0,
            hp: MATERIALS.wood.strength,
            material: 'wood',
            role: 'prop',
            color: kind === 'home' ? 0xb3a18e : kind === 'desk' ? 0xb7a081 : 0xb89365,
            bounds: { x: 0.08, y: 0, z: 0.08, w: 0.84, h: height / CELL, d: 0.84 },
          },
        ];
        const item: Building = {
          id: itemId,
          name: `${b.name} · ${furniture} ${floor + 1}-${index + 1}`,
          kind: 'furniture',
          category: 'prop',
          furniture,
          x: b.x + p.x * CELL,
          z: b.z + p.z * CELL,
          baseY: foot,
          w: 1,
          d: 1,
          h: Math.ceil(height / CELL),
          color: 0xb79573,
          accent: 0x405e62,
          cells: itemCells,
          parentCell: floorCell.id,
          chunk: b.chunk,
        };
        furnish(itemCells[0], furniture, b.id + index + floor, bedAlongRoom);
        city.props.push(item);
        const slotFloor = b.cells.find((c) => c.x === p.x && c.z === p.z - 1 && c.y === floorY);
        const place: Place = {
          id: `place:${b.id}:${floor}:${index}`,
          kind,
          name: `${b.name} · ${kind} ${index + 1}`,
          buildingId: b.id,
          floor,
          x: b.x + (p.x + 0.5) * CELL,
          y: foot,
          z: b.z + (p.z - 0.5) * CELL,
          supportCell: slotFloor?.id,
          furnitureId: item.id,
          tenant: use === 'shop' ? tenant : undefined,
          capacity: 1,
          duration:
            kind === 'desk'
              ? 28
              : kind === 'home'
                ? 22
                : kind === 'counter'
                  ? 3
                  : kind === 'display'
                    ? 7
                    : kind === 'materials'
                      ? 3
                      : 10,
        };
        if (kind === 'seat' || kind === 'home')
          place.pose = {
            x: item.x + (furniture === 'bed' && !bedAlongRoom ? 1.84 : 0.625),
            y: foot,
            z: item.z + (furniture === 'bed' ? (bedAlongRoom ? 1.84 : 0.625) : 0.57),
            yaw: furniture === 'bed' && !bedAlongRoom ? Math.PI / 2 : 0,
            posture: furniture === 'bed' ? 'lying' : 'seated',
            seatHeight: 0.49,
            duration: furniture === 'bed' ? 1.8 : 1.1,
          };
        city.places.push(place);
      }
    }
  }
  for (const b of city.props.filter((p) => p.kind === 'bench' || p.kind === 'lamp').slice(0, 14))
    city.places.push({
      id: `place:${b.id}:park`,
      kind: b.kind === 'bench' ? 'seat' : 'maintenance',
      name: b.kind === 'bench' ? 'Garden seat' : 'Lamp inspection',
      buildingId: b.id,
      floor: 0,
      x: b.x + (b.w * CELL) / 2,
      y: GROUND,
      z: b.z - 0.6,
      capacity: 1,
      duration: 10,
      supportCell: b.cells[0]?.id,
    });
  city.places.push(...(city.airport?.gate ?? []));
  prepareRoomLayouts(city);
  return city;
}
